import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Box, Paper, styled, Typography, useTheme } from '@mui/material';
import { TransformedMessage } from '@provision/chat-utils';
import { ConversationMessageDispatchStatus } from '@provision/conversation-types';
import { useMemo } from 'react';
import { Tooltip } from 'src/components';
import { DispatchStatusIcon } from './dispatch-status-icon';
import { parseDispatchFailureReason } from './dispatch-status.utils';

const TooltipShell = styled(Paper)(({ theme }) => ({
  width: 400,
  maxHeight: 500,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  overflow: 'auto',
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  background: theme.palette.action.hover,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const Row = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '40px 1fr auto',
  columnGap: theme.spacing(1),
  alignItems: 'flex-start',
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-of-type': { borderBottom: 'none' },
}));

const Label = styled(Typography)({
  fontWeight: 600,
  fontSize: 14,
});

const Value = styled(Typography)({
  overflowWrap: 'anywhere',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  fontSize: 14,
});

type MessageDispatches = NonNullable<TransformedMessage['messageDispatches']>;

const getName = (d: MessageDispatches[number]) => {
  const identifier = d.participation?.channel?.externalIdentifier ?? 'unknown';

  return d.participant?.fullName ? `${d.participant.fullName} <${identifier}>` : identifier;
};

type DispatchWithCustomAttrs = MessageDispatches[number] & { customAttributes?: string };

const getFailureReason = (d: DispatchWithCustomAttrs): string | undefined =>
  (d.dispatchStatus as ConversationMessageDispatchStatus) ===
  ConversationMessageDispatchStatus.Failed
    ? parseDispatchFailureReason(d.customAttributes)
    : undefined;

interface RecipientListProps {
  label: 'To' | 'Cc';
  dispatches: MessageDispatches;
}

const RecipientList = ({ label, dispatches }: RecipientListProps) => {
  if (dispatches.length === 0) return null;

  return (
    <Row>
      <Label>{label}</Label>
      <Box display="flex" flexDirection="column">
        {dispatches.map((d) => {
          const name = getName(d);
          const failureReason = getFailureReason(d);

          return (
            <Box key={name} display="flex" flexDirection="column">
              <Tooltip title={name} enterDelay={500}>
                <Box display="flex" alignItems="center" maxWidth={300} fontSize={16}>
                  <Value sx={{ marginRight: 0.5 }}>{name}</Value>
                  <DispatchStatusIcon dispatches={[d]} />
                </Box>
              </Tooltip>
              {failureReason ? (
                <Typography color="error" fontSize={12}>
                  {failureReason}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Row>
  );
};

export interface DispatchStatusGridProps {
  from: { fullName?: string; email?: string };
  dispatches: MessageDispatches;
  to?: { participantIdentity: string };
}

export const DispatchStatusGrid = ({ from, dispatches, to }: DispatchStatusGridProps) => {
  const theme = useTheme();

  const toDispatches = useMemo(() => {
    if (to) {
      return dispatches.filter((d) => d.participant?.id === to.participantIdentity);
    }

    return [];
  }, [dispatches, to]);

  const ccDispatches = useMemo(
    () => dispatches.filter((d) => !toDispatches.includes(d)),
    [dispatches, toDispatches],
  );

  return (
    <TooltipShell elevation={0}>
      <Header>
        <AccessTimeIcon sx={{ fontSize: 22, color: theme.palette.text.secondary }} />
        <Typography fontWeight={600} fontSize={14}>
          Message Info
        </Typography>
      </Header>

      <Row>
        <Label>From</Label>
        <Value>
          {from.fullName}
          {!!from.email && ` <${from.email}>`}
        </Value>
      </Row>

      {!!toDispatches?.length && <RecipientList label="To" dispatches={toDispatches} />}

      <RecipientList label="Cc" dispatches={ccDispatches} />
    </TooltipShell>
  );
};
